/** Angel One market depth via PMS REST polling (2s; reduce to 500ms when stable). */
export const ANGEL_ONE_MARKET_DEPTH_POLL_MS = 500;

const toLevel = (level, flag) => ({
  BbBuySellFlag: flag,
  Quantity: Number(level?.quantity) || 0,
  Price: Number(level?.price) || 0,
  NumberOfOrders: Number(level?.orders) || 0,
});

/** Maps PMS `depth.buy` / `depth.sell` to the same row shape as 5paisa WS depth. */
export function normalizeAngelMarketDepth(depth) {
  if (!depth || typeof depth !== "object") return [];
  const buy = Array.isArray(depth.buy) ? depth.buy : [];
  const sell = Array.isArray(depth.sell) ? depth.sell : [];
  const bids = buy.slice(0, 5).map((level) => toLevel(level, 66));
  const asks = sell.slice(0, 5).map((level) => toLevel(level, 83));
  while (bids.length < 5) bids.push(toLevel({}, 66));
  while (asks.length < 5) asks.push(toLevel({}, 83));
  return [...bids, ...asks];
}

function resolveAngelExchange(holding = {}) {
  const seg = String(holding?.ExchSeg || holding?.exch_seg || "").toUpperCase();
  const exch = String(holding?.Exch || holding?.exch || "").toUpperCase();
  if (exch === "B" || seg.startsWith("BSE")) return "BSE";
  if (exch === "M" || seg.includes("MCX")) return "MCX";
  return "NSE";
}

function resolveAngelSymbolToken(holding = {}) {
  return String(
    holding?.symboltoken ||
      holding?.symbol_token ||
      holding?.token ||
      holding?.Token ||
      holding?.ScripCode ||
      holding?.scripcode ||
      "",
  ).trim();
}

export async function fetchAngelOneMarketDepth({
  pmsApiBaseUrl,
  userId,
  holding,
}) {
  const exchange = resolveAngelExchange(holding);
  const symbol_token = resolveAngelSymbolToken(holding);
  if (!userId || !symbol_token) {
    throw new Error("Missing user_id or symbol_token for AngelOne market depth");
  }

  const response = await fetch(`${pmsApiBaseUrl}/api/v1/angelone/market-depth`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: Number(userId),
      exchange,
      symbol_token,
    }),
  });

  if (!response.ok) {
    throw new Error(`AngelOne market depth failed (${response.status})`);
  }

  const payload = await response.json();
  const depth = payload?.data?.depth;
  return normalizeAngelMarketDepth(depth);
}

class AngelOneMarketDepthService {
  constructor() {
    this.intervalId = null;
    this.inFlight = false;
    this.activeHolding = null;
    this.pmsApiBaseUrl = "";
    this.userId = 0;
    this.listeners = new Set();
  }

  subscribe(listener) {
    if (typeof listener !== "function") return () => {};
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  emit(depth) {
    const rows = Array.isArray(depth) ? depth : [];
    this.listeners.forEach((listener) => {
      try {
        listener(rows);
      } catch (error) {
        console.error("[AngelOne-MD] listener error:", error);
      }
    });
  }

  openDepth(holding, { pmsApiBaseUrl, userId }) {
    this.closeDepth({ clearListeners: false });
    this.activeHolding = holding;
    this.pmsApiBaseUrl = pmsApiBaseUrl || "";
    this.userId = Number(userId) || 0;

    if (!this.activeHolding || !this.pmsApiBaseUrl || !this.userId) {
      return;
    }

    this.pollOnce();
    this.intervalId = setInterval(
      () => this.pollOnce(),
      ANGEL_ONE_MARKET_DEPTH_POLL_MS,
    );
  }

  closeDepth({ clearListeners = false } = {}) {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.inFlight = false;
    this.activeHolding = null;
    this.pmsApiBaseUrl = "";
    this.userId = 0;
    this.emit([]);
    if (clearListeners) {
      this.listeners.clear();
    }
  }

  async pollOnce() {
    if (!this.activeHolding || this.inFlight || !this.pmsApiBaseUrl || !this.userId) {
      return;
    }

    this.inFlight = true;
    try {
      const depth = await fetchAngelOneMarketDepth({
        pmsApiBaseUrl: this.pmsApiBaseUrl,
        userId: this.userId,
        holding: this.activeHolding,
      });
      if (this.activeHolding) {
        this.emit(depth);
      }
    } catch (error) {
      console.error("[AngelOne-MD] poll error:", error);
    } finally {
      this.inFlight = false;
    }
  }
}

const angelOneMarketDepthService = new AngelOneMarketDepthService();

export default angelOneMarketDepthService;
